import { useState } from 'react';
import { Send, Upload, X, CheckCircle, AlertCircle, Star } from 'lucide-react';
import { useUserPreferences } from '@/store/useStore';
import { T } from './DashboardShell';

const card: React.CSSProperties = {
  background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16,
};
const label: React.CSSProperties = {
  display: 'block', fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em',
  color: T.text35, marginBottom: 8, textTransform: 'uppercase',
};
const field: React.CSSProperties = {
  width: '100%', background: 'rgba(255,255,255,.02)', border: `1px solid ${T.border}`,
  borderRadius: 10, padding: '10px 12px', color: T.text, fontFamily: T.sans, fontSize: 13, outline: 'none',
};

export function FeedbackForm() {
  const userPreferences = useUserPreferences();
  const [feedbackType, setFeedbackType] = useState<'bug' | 'suggestion' | 'other'>('suggestion');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages = Array.from(e.target.files).slice(0, 3 - images.length);
      setImages([...images, ...newImages]);
    }
  };

  const removeImage = (index: number) => setImages(images.filter((_, i) => i !== index));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const formData = new FormData();
      formData.append('_subject', `[UniSchedule Feedback] ${feedbackType.toUpperCase()} - ${subject}`);
      formData.append('_template', 'box');
      formData.append('_captcha', 'false');
      formData.append('Feedback Type', feedbackType.toUpperCase());
      formData.append('Rating', `${rating}/5 stars`);
      formData.append('Subject', subject);
      formData.append('Name', name || userPreferences?.name || 'Not provided');
      formData.append('Email', email || 'Not provided');
      formData.append('Program', userPreferences?.program || 'N/A');
      formData.append('Department', userPreferences?.department || 'N/A');
      formData.append('Semester', String(userPreferences?.semester || 'N/A'));
      formData.append('Group', userPreferences?.group || 'N/A');
      formData.append('Message', message);
      images.forEach((image, index) => {
        formData.append(`screenshot_${index + 1}`, image);
      });

      const response = await fetch('https://formsubmit.co/faisalh5556@gmail.com', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'application/json' },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          setName(''); setEmail(''); setSubject(''); setMessage('');
          setImages([]); setRating(0); setFeedbackType('suggestion'); setSubmitStatus('idle');
        }, 3000);
      } else {
        throw new Error('Failed to send feedback');
      }
    } catch (error: any) {
      setSubmitStatus('error');
      setErrorMessage('Failed to send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, width: '100%' }}>
      {/* Status messages */}
      {submitStatus === 'success' && (
        <div style={{ ...card, borderLeft: '3px solid #10B981', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckCircle size={16} color="#10B981" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Feedback Sent!</div>
            <div style={{ fontSize: 11, color: T.text35 }}>Thank you for your feedback.</div>
          </div>
        </div>
      )}
      {submitStatus === 'error' && (
        <div style={{ ...card, borderLeft: '3px solid #EF4444', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={16} color="#EF4444" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Failed to Send</div>
            <div style={{ fontSize: 11, color: T.text35 }}>{errorMessage}</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Feedback type */}
        <div style={card}>
          <span style={label}>Feedback Type</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ value: 'bug', label: 'Bug' }, { value: 'suggestion', label: 'Suggestion' }, { value: 'other', label: 'Other' }].map(({ value, label: l }) => {
              const active = feedbackType === value;
              return (
                <button key={value} type="button" onClick={() => setFeedbackType(value as any)}
                  style={{
                    flex: 1, padding: '9px 0', borderRadius: 9, cursor: 'pointer',
                    background: active ? T.tealBg : 'rgba(255,255,255,.02)',
                    border: `1px solid ${active ? T.tealBorder : T.border}`,
                    color: active ? T.teal : T.text50, fontFamily: T.sans, fontSize: 12, fontWeight: 500, transition: '.15s',
                  }}>{l}</button>
              );
            })}
          </div>
        </div>

        {/* Rating */}
        <div style={card}>
          <span style={label}>Rate Your Experience</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {[1, 2, 3, 4, 5].map(star => (
              <button key={star} type="button" onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)} onMouseLeave={() => setHoveredRating(0)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
                <Star size={24}
                  fill={star <= (hoveredRating || rating) ? '#F59E0B' : 'none'}
                  color={star <= (hoveredRating || rating) ? '#F59E0B' : T.text25} />
              </button>
            ))}
            {rating > 0 && <span style={{ marginLeft: 6, fontFamily: T.mono, fontSize: 11, color: T.text50 }}>{rating}/5</span>}
          </div>
        </div>

        {/* Name & email */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <span style={label}>Name (Optional)</span>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={field} />
          </div>
          <div>
            <span style={label}>Email (Optional)</span>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your.email@example.com" style={field} />
          </div>
        </div>

        {/* Subject & message */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <span style={label}>Subject *</span>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description" required style={field} />
          </div>
          <div>
            <span style={label}>Message *</span>
            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your feedback in detail..." rows={4} required
              style={{ ...field, resize: 'none' }} />
          </div>
        </div>

        {/* Image upload */}
        <div style={card}>
          <span style={label}>Screenshots (Optional)</span>
          {images.length < 3 && (
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,.02)', border: `1px dashed ${T.border}`,
              borderRadius: 10, padding: 16, cursor: 'pointer',
            }}>
              <div style={{ textAlign: 'center' }}>
                <Upload size={18} color={T.text35} style={{ margin: '0 auto 6px', display: 'block' }} />
                <span style={{ fontSize: 11, color: T.text35 }}>Click to upload</span>
              </div>
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
            </label>
          )}
          {images.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 12 }}>
              {images.map((image, index) => (
                <div key={index} style={{ position: 'relative' }}>
                  <img src={URL.createObjectURL(image)} alt="" style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 10 }} />
                  <button type="button" onClick={() => removeImage(index)}
                    style={{
                      position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%',
                      background: '#EF4444', border: 'none', color: '#fff', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <button type="submit" disabled={isSubmitting || !subject || !message}
          style={{
            width: '100%', padding: '13px', borderRadius: 10,
            background: T.tealBg, border: `1px solid ${T.tealBorder}`, color: T.teal,
            fontFamily: T.mono, fontSize: 11, letterSpacing: '.1em', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            opacity: (isSubmitting || !subject || !message) ? 0.45 : 1,
          }}>
          {isSubmitting ? (
            <><span style={{ width: 14, height: 14, border: `2px solid ${T.teal}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'fbspin .7s linear infinite' }} /> SENDING…</>
          ) : (
            <><Send size={14} /> SEND FEEDBACK</>
          )}
        </button>
      </form>
      <style>{`@keyframes fbspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
